import React, { useState } from 'react';
import { Box, Button, Checkbox, Collapse, FormControlLabel, Typography } from '@mui/material';
import ToolScopePicker from './ToolScopePicker';
import type { ToolLoadoutDraft } from '../hooks/useAgentSpecForm';

// The non-catalog building blocks a loadout may carry (ALLOWED_LOADOUT_BLOCKS).
const BLOCKS: { key: string; label: string }[] = [
  { key: 'report_tool', label: 'Report tool' },
];

interface AgentToolsProps {
  loadout: ToolLoadoutDraft | null;
  /** The only tools this agent can be given; null means the whole catalog. */
  loadoutSelectable: string[] | null;
  /** The server's reason the wall will refuse a tool where this agent runs, by action_id. */
  loadoutWarnings?: Record<string, string>;
  loadoutLoading: boolean;
  /** A retired agent's toolbox is shown, not edited (PS-UC-18 4d). */
  readOnly: boolean;
  onChangeLoadout: (next: ToolLoadoutDraft) => void;
}

/**
 * The toolbox an agent runs: what it is, and the picker that changes it.
 *
 * Its own tab rather than a section under the settings, because the picker and the prompt
 * are each tall enough to own the pane, and a page holding both is a page of scrollbars.
 */
const AgentTools: React.FC<AgentToolsProps> = ({
  loadout,
  loadoutSelectable,
  loadoutWarnings,
  loadoutLoading,
  readOnly,
  onChangeLoadout,
}) => {
  const [toolsOpen, setToolsOpen] = useState(true);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Tools:{' '}
            {loadoutLoading || !loadout
              ? 'reading what this agent runs...'
              : `${loadout.tools.length} selected`}
          </Typography>
          {!readOnly && (
            <Button size="small" onClick={() => setToolsOpen((open) => !open)} sx={{ textTransform: 'none' }}>
              {toolsOpen ? 'Hide tools' : 'Choose tools'}
            </Button>
          )}
        </Box>

        {readOnly && loadout && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {loadout.tools.map((id) => (
              <li key={id}><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{id}</Typography></li>
            ))}
          </Box>
        )}

        <Collapse in={toolsOpen && !readOnly} unmountOnExit>
          {loadout && (
            <Box sx={{ mt: 1 }}>
              {/* No height of its own: the box grows to whatever the filter column needs,
                  so that column never scrolls inside a box that scrolls inside the pane.
                  Only the tool list scrolls, and it caps itself. */}
              <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <ToolScopePicker
                  selectableActionIds={loadoutSelectable}
                  selectedActionIds={loadout.tools}
                  toolWarnings={loadoutWarnings}
                  onToggle={(actionId) => onChangeLoadout({
                    ...loadout,
                    tools: loadout.tools.includes(actionId)
                      ? loadout.tools.filter((id) => id !== actionId)
                      : [...loadout.tools, actionId],
                  })}
                  onToggleMany={(actionIds, select) => onChangeLoadout({
                    ...loadout,
                    tools: select
                      ? Array.from(new Set([...loadout.tools, ...actionIds]))
                      : loadout.tools.filter((id) => !actionIds.includes(id)),
                  })}
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Also give it</Typography>
                {BLOCKS.map((block) => (
                  <FormControlLabel
                    key={block.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={loadout.blocks.includes(block.key)}
                        onChange={() => onChangeLoadout({
                          ...loadout,
                          blocks: loadout.blocks.includes(block.key)
                            ? loadout.blocks.filter((b) => b !== block.key)
                            : [...loadout.blocks, block.key],
                        })}
                      />
                    }
                    label={<Typography variant="caption">{block.label}</Typography>}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={loadout.avp_visibility_filter}
                      onChange={(e) => onChangeLoadout({ ...loadout, avp_visibility_filter: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption">Trim to the operator&apos;s own access</Typography>}
                />
              </Box>
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};

export default AgentTools;
